import type { ComponentProps, ReactNode } from "react";
import { Slot } from "@radix-ui/react-slot";
import { tv, type VariantProps } from "tailwind-variants";

import { Spinner } from "../components/spinner";
import { IconWrapper } from "./icon-wrapper";
import { focusRing } from "./interaction";

const buttonVariants = tv({
  base: [
    "relative flex cursor-pointer items-center justify-center gap-2 rounded-full border border-transparent font-semibold",
    "transition-colors duration-150",
    focusRing,
    "disabled:cursor-not-allowed",
  ],
  variants: {
    variant: {
      primary:
        "bg-surface-inverted text-content-on-inverted hover:bg-surface-inverted-hover disabled:bg-surface-muted disabled:text-content-subtle",
      /* Sits on top of imagery and dark blocks, so it borrows the surrounding text color. */
      secondary:
        "border-current text-current hover:bg-current/10 disabled:border-content-subtle disabled:text-content-subtle",
      "secondary-dark":
        "border-line-inverted text-content hover:bg-surface-hover disabled:border-line disabled:text-content-subtle",
      tertiary: "text-content hover:bg-surface-hover disabled:text-content-subtle",
      danger: "bg-danger text-danger-content hover:bg-danger-hover disabled:bg-surface-muted",
      /* No chrome at all — for icon buttons and inline text triggers. */
      focus: "min-h-0 border-0 p-0",
    },
    size: {
      sm: "min-h-11 px-4 py-2 text-sm",
      md: "min-h-12 px-5 py-2.5 text-base leading-5",
      lg: "min-h-14 px-7 py-4 text-lg leading-[22px]",
      tight: "",
    },
  },
});

export const buttonSizes = ["sm", "md", "lg"] as const;
export type ButtonSize = (typeof buttonSizes)[number];

export type ButtonProps = {
  left?: ReactNode;
  right?: ReactNode;
  /** The child is rendered as-is, so `left` and `right` are dropped: put the icon in the child. */
  asChild?: boolean;
  isLoading?: boolean;
} & ComponentProps<"button"> &
  VariantProps<typeof buttonVariants>;

export const Button = ({
  asChild = false,
  children,
  className,
  disabled,
  isLoading = false,
  left,
  right,
  size = "md",
  type = "button",
  variant = "primary",
  ...props
}: ButtonProps) => {
  const isDisabled = disabled || isLoading;
  const content: ReactNode = (
    <>
      {left ? <IconWrapper size={size}>{left}</IconWrapper> : null}
      {children}
      {right ? <IconWrapper size={size}>{right}</IconWrapper> : null}
    </>
  );

  /*
    asChild hands the element over to the child: it owns its own `type`. A
    <button> child inside a form must set type="button" itself, or it defaults
    to submit — Slot's props are typed as HTMLAttributes and cannot carry it.
  */
  if (asChild) {
    return (
      <Slot
        aria-busy={isLoading || undefined}
        aria-disabled={isDisabled || undefined}
        className={buttonVariants({ className, variant, size })}
        {...props}
      >
        {isLoading ? (
          <Spinner className="size-4 border-2 border-current border-t-transparent" />
        ) : (
          children
        )}
      </Slot>
    );
  }

  return (
    <button
      aria-busy={isLoading || undefined}
      className={buttonVariants({ className, variant, size })}
      disabled={isDisabled}
      type={type}
      {...props}
    >
      {/* Kept mounted while loading so the button never changes width mid-request. */}
      <span className={isLoading ? "invisible flex items-center gap-2" : "contents"}>
        {content}
      </span>

      {isLoading ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner className="size-4 border-2 border-current border-t-transparent" />
          <span className="sr-only">Loading</span>
        </span>
      ) : null}
    </button>
  );
};
